import { ArrowDownLeft, ArrowUpRight, QrCode, SearchX } from "lucide-react";

import { IncidentStatusPill } from "@/modules/dashboard/payment-incidents/components/IncidentStatusPill";
import {
  incidentProviderName,
  isIncidentOpen,
} from "@/modules/dashboard/payment-incidents/utils/paymentIncidents";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { compareDecimalStrings } from "@/shared/utils/decimal";
import { formatRail } from "@/shared/utils/railDisplay";

import { useOperationLabels } from "../../hooks/useOperationLabels";
import { useWalletEntryLabels } from "../../hooks/useWalletEntryLabels";
import { WALLET_MOVEMENTS_PAGE_SIZE } from "../../hooks/useWalletMovements";
import type { PaymentMovement } from "../../types/payments-wallets.types";
import { resolveLocale } from "../../utils/formatWalletAmount";
import { overviewFractionDigits } from "../../utils/walletsOverview";

export interface MovementsTableProps {
  rows: PaymentMovement[];
  total: number;
  page: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  masked: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onOpen: (movement: PaymentMovement) => void;
}

/** Every client wallet's movements, newest first; a row opens its detail and its incident. */
export function MovementsTable({
  rows,
  total,
  page,
  onPageChange,
  isLoading,
  masked,
  hasActiveFilters,
  onClearFilters,
  onOpen,
}: MovementsTableProps) {
  const { t, activeLanguage } = useTranslations("wallets");
  const { t: tIncidents } = useTranslations("paymentIncidents");
  const operationLabels = useOperationLabels();
  const { statusOf, describe } = useWalletEntryLabels();
  const locale = resolveLocale(activeLanguage);

  const when = (movement: PaymentMovement) => (
    <span className="flex flex-col">
      <span className="text-sm text-foreground tabular-nums">
        {new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" }).format(
          new Date(movement.posted_at),
        )}
      </span>
      <span className="text-[11px] text-muted-foreground tabular-nums">
        {new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(
          new Date(movement.posted_at),
        )}
      </span>
    </span>
  );

  const organization = (movement: PaymentMovement) => (
    <span className="flex min-w-0 flex-col">
      <span
        className="block max-w-52 truncate text-sm font-medium text-foreground"
        title={movement.organization_name ?? ""}
      >
        {movement.organization_name ?? movement.organization_id}
      </span>
      <span className="text-[11px] text-muted-foreground">{movement.currency}</span>
    </span>
  );

  const what = (movement: PaymentMovement) => {
    const who =
      movement.operation_beneficiary_name ??
      (movement.breb_key_value ? `${movement.breb_key_type ?? ""} ${movement.breb_key_value}`.trim() : null);
    return (
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm text-foreground">
            {/* An adjustment says which one (a Coloca top-up, a refund), as the client's statement does. */}
            {(movement.operation_type === "ADJUST" ? describe(movement) : null) ??
              operationLabels[movement.operation_type] ??
              movement.operation_type}
          </span>
          {movement.operation_rail && (
            <ColocaBadge variant="secondary" className="text-[10px]">
              {formatRail(movement.operation_rail)}
            </ColocaBadge>
          )}
          {movement.breb_qr && (
            <QrCode className="size-3.5 text-muted-foreground" aria-label={t("movements_breb_qr")} />
          )}
        </span>
        {who && (
          <span className="block max-w-60 truncate text-[11px] text-muted-foreground" title={who}>
            {who}
          </span>
        )}
      </span>
    );
  };

  const amount = (movement: PaymentMovement) => {
    const inbound = movement.direction === "C";
    const digits = minorUnitsFor(movement.currency);
    const delivered =
      movement.operation_amount &&
      movement.operation_currency &&
      (movement.operation_currency !== movement.currency ||
        compareDecimalStrings(movement.operation_amount, movement.amount) !== 0)
        ? { value: movement.operation_amount, currency: movement.operation_currency }
        : null;
    return (
      <span className="flex flex-col items-end gap-0.5">
        <span className={cn("flex items-center gap-1", inbound ? "text-primary" : "text-foreground")}>
          {inbound ? (
            <ArrowDownLeft className="size-3" aria-hidden="true" />
          ) : (
            <ArrowUpRight className="size-3" aria-hidden="true" />
          )}
          <ColocaAmountDisplay
            value={inbound ? movement.amount : `-${movement.amount}`}
            currency={movement.currency}
            fractionDigits={digits}
            locale={locale}
            size="sm"
            signed
            masked={masked}
          />
        </span>
        {delivered && (
          <span className="text-[11px] text-muted-foreground">
            {t(inbound ? "movements_operation_amount_in" : "movements_operation_amount_out")}{" "}
            <ColocaAmountDisplay
              value={delivered.value}
              currency={delivered.currency}
              fractionDigits={overviewFractionDigits(delivered.currency)}
              locale={locale}
              size="xs"
              masked={masked}
            />
          </span>
        )}
      </span>
    );
  };

  const status = (movement: PaymentMovement) => (
    <span className="text-xs text-muted-foreground">{statusOf(movement) ?? "—"}</span>
  );

  const incident = (movement: PaymentMovement) => {
    const current = movement.incident;
    if (!current) return <span className="text-xs text-muted-foreground/70">—</span>;
    const provider = incidentProviderName(movement.provider_code);
    const days = current.days_since_update;
    return (
      <span className="flex flex-col items-start gap-0.5">
        <IncidentStatusPill status={current.status} provider={provider} size="sm" />
        <span className="text-[11px] text-muted-foreground">
          {current.reference}
          {isIncidentOpen(current.status) &&
            days > 0 &&
            ` · ${tIncidents(days === 1 ? "movements_days_quiet_one" : "movements_days_quiet_other", { count: days })}`}
          {current.client_refunded && ` · ${tIncidents("movements_client_refunded")}`}
        </span>
      </span>
    );
  };

  const columns: ColocaDataTableColumn<PaymentMovement>[] = [
    { id: "when", header: t("movements_col_when"), cell: when },
    { id: "organization", header: t("col_organization"), cell: organization, cellClassName: "max-w-52" },
    { id: "what", header: t("movements_col_what"), cell: what, cellClassName: "max-w-64" },
    {
      id: "amount",
      header: t("movements_col_amount"),
      headerClassName: "text-right",
      cellClassName: "text-right",
      cell: amount,
    },
    { id: "status", header: t("col_status"), cell: status },
    { id: "incident", header: t("movements_col_incident"), cell: incident },
  ];

  const empty = (
    <ColocaEmpty
      surface="card"
      icon={SearchX}
      title={t("movements_empty")}
      description={hasActiveFilters ? t("movements_empty_filtered") : undefined}
    >
      {hasActiveFilters && (
        <ColocaButton variant="secondary" size="sm" onClick={onClearFilters}>
          {t("filter_clear")}
        </ColocaButton>
      )}
    </ColocaEmpty>
  );

  const pagination =
    total > WALLET_MOVEMENTS_PAGE_SIZE ? (
      <ColocaPagination
        page={page}
        perPage={WALLET_MOVEMENTS_PAGE_SIZE}
        total={total}
        onPageChange={onPageChange}
        withSeparator={false}
        size="sm"
        summary={t("movements_total", { total: new Intl.NumberFormat(activeLanguage).format(total) })}
      />
    ) : undefined;

  return (
    <>
      <div className="hidden xl:block">
        <ColocaDataTable
          columns={columns}
          rows={rows}
          rowKey={(movement) => movement.id}
          onRowClick={onOpen}
          loading={isLoading && rows.length === 0}
          minWidth={1080}
          empty={empty}
          footer={pagination}
          rowClassName={(movement) =>
            movement.incident && isIncidentOpen(movement.incident.status) ? "bg-warning/5" : undefined
          }
          className={isLoading && rows.length > 0 ? "opacity-60 transition-opacity" : undefined}
        />
      </div>

      <div className="space-y-3 xl:hidden">
        {isLoading && rows.length === 0 ? (
          Array.from({ length: 3 }, (_, index) => <ColocaSkeleton key={index} className="h-32 rounded-xl" />)
        ) : rows.length === 0 ? (
          empty
        ) : (
          <>
            <ul className="space-y-3">
              {rows.map((movement) => (
                <li key={movement.id}>
                  <button
                    type="button"
                    onClick={() => onOpen(movement)}
                    className="w-full space-y-3 rounded-xl border border-border bg-card p-4 text-left focus-visible:outline-2 focus-visible:outline-ring"
                  >
                    <span className="flex items-start justify-between gap-3">
                      {organization(movement)}
                      {amount(movement)}
                    </span>
                    <span className="flex items-end justify-between gap-3">
                      {what(movement)}
                      {when(movement)}
                    </span>
                    <span className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                      {status(movement)}
                      {incident(movement)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {pagination}
          </>
        )}
      </div>
    </>
  );
}
